import { IMobAttackRequest } from "@/store/apis/mobsAPI/mobs.types"

export enum BATTLE_STATE {
  OPEN = "OPEN",
  CLOSED = "CLOSED"
}

export enum BATTLE_OUTCOME {
  WIN = "WIN",
  LOSS = "LOSS",
  FLEE = "FLEE"
}

export enum BATTLE_END_REASON {
  ELIMINATION = "ELIMINATION",
  PLAYER_FLED = "PLAYER_FLED",
  ABANDONED = "ABANDONED",
  ROUND_CAP = "ROUND_CAP"
}

export enum BATTLE_FIGHTER_KIND {
  HERO = "HERO",
  MOB = "MOB"
}

export enum BATTLE_TEAM {
  A = "A",
  B = "B"
}

export enum BATTLE_ROUND_RESOLVER {
  PLAYER = "PLAYER",
  AUTO = "AUTO",
  TIMEOUT = "TIMEOUT"
}

export enum BATTLE_ACTION_TYPE {
  ATTACK = "ATTACK",
  ABILITY_ONLY = "ABILITY_ONLY",
  MOVE = "MOVE"
}

export enum BATTLE_MOVE_DIRECTION {
  UP = "UP",
  DOWN = "DOWN",
  LEFT = "LEFT",
  RIGHT = "RIGHT"
}

export enum COMBAT_EVENT_TYPE {
  STRIKE = "STRIKE",
  ELIMINATION = "ELIMINATION",
  MOVE = "MOVE",
  ROUND_END = "ROUND_END",
  BATTLE_END = "BATTLE_END",
  EFFECT_TICK = "EFFECT_TICK",
  ABILITY_CAST = "ABILITY_CAST",
  ABILITY_HIT = "ABILITY_HIT",
  ABILITY_EFFECT_APPLIED = "ABILITY_EFFECT_APPLIED"
}

export enum ABILITY_CAST_OUTCOME {
  SUCCESS = "SUCCESS",
  NO_ENERGY = "NO_ENERGY",
  ON_COOLDOWN = "ON_COOLDOWN",
  NO_VALID_TARGET = "NO_VALID_TARGET"
}

export enum ABILITY_EFFECT_KIND {
  DIRECT_DAMAGE = "DIRECT_DAMAGE",
  DOT = "DOT",
  HOT = "HOT",
  BUFF = "BUFF",
  DEBUFF = "DEBUFF",
  SHIELD = "SHIELD",
  CONTROL = "CONTROL",
  HEAL = "HEAL"
}

export enum BATTLE_DENIAL_REASON {
  HP_TOO_LOW = "HP_TOO_LOW",
  ROUND_RESOLVED = "ROUND_RESOLVED",
  AUTO_BATTLE_ON = "AUTO_BATTLE_ON",
  BATTLE_CLOSED = "BATTLE_CLOSED",
  ACTION_RETRY_EXHAUSTED = "ACTION_RETRY_EXHAUSTED"
}

export enum BATTLE_EVENT_TYPE {
  BATTLE_STARTED = "BATTLE_STARTED",
  BATTLE_ROUND_RESOLVED = "BATTLE_ROUND_RESOLVED",
  BATTLE_AUTO_TOGGLED = "BATTLE_AUTO_TOGGLED",
  BATTLE_FINISHED = "BATTLE_FINISHED"
}

export enum BATTLE_COLUMN {
  C1 = "C1",
  C2 = "C2",
  C3 = "C3",
  C4 = "C4"
}

export enum BATTLE_ROW {
  R1 = "R1",
  R2 = "R2",
  R3 = "R3",
  R4 = "R4",
  R5 = "R5"
}

export enum FIGHTER_ARCHETYPE {
  SPEARMAN = "SPEARMAN",
  ROGUE = "ROGUE",
  SWORDSMAN = "SWORDSMAN",
  TANK = "TANK",
  MAGE = "MAGE",
  BUFFER = "BUFFER"
}

export enum FIGHTER_WEAPON_TYPE {
  TWO_HANDED_SPEAR = "TWO_HANDED_SPEAR",
  DUAL_KNIVES = "DUAL_KNIVES",
  DUAL_SWORDS = "DUAL_SWORDS",
  SWORD_AND_SHIELD = "SWORD_AND_SHIELD",
  STAFF_AND_ORB = "STAFF_AND_ORB"
}

export enum WARRIOR_SPECIALIZATION {
  CRIT = "CRIT",
  CRIT_ACCURACY = "CRIT_ACCURACY",
  DODGE = "DODGE",
  DODGE_ACCURACY = "DODGE_ACCURACY",
  DODGE_CRIT = "DODGE_CRIT",
  RESILIENCE_ACCURACY = "RESILIENCE_ACCURACY",
  RESILIENCE_CRIT = "RESILIENCE_CRIT"
}

export enum MOVE_STATUS {
  SUCCESS = "SUCCESS",
  COLLISION_LOST = "COLLISION_LOST",
  OUT_OF_BOUNDS = "OUT_OF_BOUNDS",
  CELL_OCCUPIED = "CELL_OCCUPIED",
  INVALID_TEAM_BOUNDARY = "INVALID_TEAM_BOUNDARY"
}

export interface IBattleCell {
  column: BATTLE_COLUMN
  row: BATTLE_ROW
}

export interface IBattleGridCell extends IBattleCell {
  fighterId: string | null
}

export interface IBattleAbility {
  id: string
  energyCost: number
  cooldownRounds: number
}

export interface IBattleFighter {
  fighterId: string
  team: BATTLE_TEAM
  kind: BATTLE_FIGHTER_KIND
  alive: boolean
  hp: number
  maxHp: number
  mana: number | null
  maxMana: number | null
  energy: number
  maxEnergy: number
  position: string | null
  archetype: FIGHTER_ARCHETYPE
  weaponType: FIGHTER_WEAPON_TYPE
  specialization: WARRIOR_SPECIALIZATION | null
  cell: IBattleCell | null
  abilities: IBattleAbility[]
  cooldowns: Record<string, number>
  effects: unknown[]
}

export interface IStrikeResult {
  hit: boolean
  crit: boolean
  armorPenetrated: boolean
  damage: number
  fizzled: boolean
}

export interface IStrikeEvent {
  type: COMBAT_EVENT_TYPE.STRIKE
  round: number
  attackerId: string
  targetId: string
  result: IStrikeResult
}

export interface IEliminationEvent {
  type: COMBAT_EVENT_TYPE.ELIMINATION
  round: number
  characterId: string
  killedBy: string
}

export interface IMoveEvent {
  type: COMBAT_EVENT_TYPE.MOVE
  round: number
  characterId: string
  from: IBattleCell
  to: IBattleCell
  status: MOVE_STATUS
}

export interface IRoundEndEvent {
  type: COMBAT_EVENT_TYPE.ROUND_END
  round: number
  hpSnapshot: Record<string, number>
}

export interface IBattleEndEvent {
  type: COMBAT_EVENT_TYPE.BATTLE_END
  round: number
  winner: BATTLE_TEAM | null
}

export interface IEffectTickEvent {
  type: COMBAT_EVENT_TYPE.EFFECT_TICK
  round: number
  characterId: string
  effectId: string
  damage: number
  heal: number
  remainingHp: number
}

export interface IAbilityCastEvent {
  type: COMBAT_EVENT_TYPE.ABILITY_CAST
  round: number
  casterId: string
  abilityId: string
  targetIds: string[]
  energySpent: number
  outcome: ABILITY_CAST_OUTCOME
}

export interface IAbilityHitEvent {
  type: COMBAT_EVENT_TYPE.ABILITY_HIT
  round: number
  casterId: string
  targetId: string
  abilityId: string
  kind: ABILITY_EFFECT_KIND
  amount: number
  remainingHp: number
}

export interface IAbilityEffectAppliedEvent {
  type: COMBAT_EVENT_TYPE.ABILITY_EFFECT_APPLIED
  round: number
  casterId: string
  targetId: string
  abilityId: string
  effectId: string
  kind: ABILITY_EFFECT_KIND
  durationRounds: number
}

export type ICombatEvent =
  | IStrikeEvent
  | IEliminationEvent
  | IMoveEvent
  | IRoundEndEvent
  | IBattleEndEvent
  | IEffectTickEvent
  | IAbilityCastEvent
  | IAbilityHitEvent
  | IAbilityEffectAppliedEvent

export interface IBattleRound {
  round: number
  resolvedBy: BATTLE_ROUND_RESOLVER
  events: ICombatEvent[]
}

export interface IBattleView {
  id: string
  state: BATTLE_STATE
  outcome: BATTLE_OUTCOME | null
  endReason: BATTLE_END_REASON | null
  killId: string | null
  round: number
  roundDeadline: string | null
  auto: boolean
  missedRounds: number
  heroFighterId: string
  fighters: IBattleFighter[]
  grid: IBattleGridCell[]
  lastRound: IBattleRound | null
}

export interface IOpenBattleResponse {
  battle: IBattleView | null
}

export interface IBattleLegalActions {
  attackTargetIds: string[]
  moveDirections: BATTLE_MOVE_DIRECTION[]
  attackAbilityIds: string[]
  standaloneAbilityIds: string[]
}

export type IStartBattleRequest = IMobAttackRequest

export interface IBattleActionRequest {
  type: BATTLE_ACTION_TYPE
  targetId?: string
  abilityId?: string
  abilityTargetId?: string
  direction?: BATTLE_MOVE_DIRECTION
}

export interface ISubmitBattleActionArgs {
  battleId: string
  round: number
  action: IBattleActionRequest
}

export interface ISetBattleAutoArgs {
  battleId: string
  enabled: boolean
}

export interface IGetLegalActionsArgs {
  battleId: string
  round: number
}

export interface IBattleEvent<TData> {
  type: BATTLE_EVENT_TYPE
  playerId: string
  occurredAt: string
  data: TData
}

export interface IBattleRoundResolvedData {
  battleId: string
  round: number
  resolvedBy: BATTLE_ROUND_RESOLVER
  events: ICombatEvent[]
  hpSnapshot: Record<string, number>
  nextRound: number | null
  nextDeadline: string | null
}

export interface IBattleAutoToggledData {
  battleId: string
  auto: boolean
  nextDeadline: string | null
}

export interface IBattleFinishedData {
  battleId: string
  outcome: BATTLE_OUTCOME
  endReason: BATTLE_END_REASON
  killId: string | null
}

export interface IBattleDenialBody {
  statusCode: number
  message: string
  reason: BATTLE_DENIAL_REASON
}
